import { zodResolver } from "@hookform/resolvers/zod"
import { Box, IconButton, Paper, Typography } from "@mui/material"
import { ChevronLeftOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import PaymentTermForm from "../components/PaymentTermForm"
import type { PaymentTermsSchemaFormValues } from "../schemas/PaymentTermsSchema"
import { PaymentTermsSchema } from "../schemas/PaymentTermsSchema"
import { buildDefaultFormValues } from "../utils/paymentTerms"

import { Button } from "@/components/common/Button"
import type { PaymentTerm } from "@/graphql/codegen/graphql"

interface PaymentTermProps {
  paymentTerm?: PaymentTerm
  onSubmit: (data: PaymentTermsSchemaFormValues) => Promise<void>
}

const PaymentTermContainer: FC<PaymentTermProps> = ({ onSubmit, paymentTerm }) => {
  const navigate = useNavigate()

  const methods = useForm<PaymentTermsSchemaFormValues>({
    values: buildDefaultFormValues(paymentTerm),
    resolver: zodResolver(PaymentTermsSchema),
    mode: "all",
  })
  const {
    handleSubmit,
    formState: { isValid },
  } = methods

  const handleSubmitForm = async (data: PaymentTermsSchemaFormValues) => {
    await onSubmit(data)
    await navigateToPaymentTerms()
  }

  const navigateToPaymentTerms = async () => await navigate({ to: "/app/admin/accounting/payment-terms" })

  return (
    <div className='flex h-full flex-col gap-4'>
      <Paper elevation={0} className='flex min-h-0 grow flex-col space-y-8 overflow-hidden bg-white'>
        <FormProvider {...methods}>
          <Box className='flex items-center justify-between gap-4'>
            <Box className='flex items-center gap-2'>
              <IconButton
                aria-label='back to contacts'
                className='rounded bg-gray-200 text-primary'
                onClick={() => navigateToPaymentTerms()}
              >
                <ChevronLeftOutlined />
              </IconButton>
              <Typography variant='h5' color='primary' fontWeight='normal'>
                {paymentTerm ? "Edit" : "Create"} Payment Terms
              </Typography>
            </Box>

            <Button appearance='contained' onClick={handleSubmit(handleSubmitForm)} disabled={!isValid}>
              Save And Exit
            </Button>
          </Box>
          <PaymentTermForm />
        </FormProvider>
      </Paper>
    </div>
  )
}

export default PaymentTermContainer
